/**
 * Industry / field segments for portfolio projects.
 * Set per project in frontmatter: `segments: [finance, automotive]`
 * or via PROJECT_SEGMENT_ASSIGNMENTS when not in markdown yet.
 */

export const PROJECT_SEGMENTS = [
  { id: "healthcare", label: "Healthcare" },
  { id: "finance", label: "Finance" },
  { id: "automotive", label: "Automotive" },
  { id: "technology", label: "Technology" },
  { id: "ecommerce", label: "E-commerce" },
  { id: "hospitality", label: "Hospitality" },
  { id: "food-beverage", label: "Food & beverage" },
  { id: "education", label: "Education" },
  { id: "government", label: "Government" },
  { id: "marketing", label: "Marketing" },
  { id: "entertainment", label: "Entertainment" },
  { id: "real-estate", label: "Real estate" },
  { id: "lifestyle", label: "Lifestyle" },
  { id: "other", label: "Other" },
] as const;

export type ProjectSegmentId = (typeof PROJECT_SEGMENTS)[number]["id"];

const SEGMENT_IDS = new Set<string>(PROJECT_SEGMENTS.map((s) => s.id));

const SEGMENT_ALIASES: Record<string, ProjectSegmentId> = {
  health: "healthcare",
  medical: "healthcare",
  pharma: "healthcare",
  financial: "finance",
  fintech: "finance",
  banking: "finance",
  economy: "finance",
  automoto: "automotive",
  "auto-moto": "automotive",
  auto: "automotive",
  moto: "automotive",
  rental: "automotive",
  cars: "automotive",
  it: "technology",
  tech: "technology",
  software: "technology",
  saas: "technology",
  computers: "technology",
  data: "technology",
  shop: "ecommerce",
  retail: "ecommerce",
  eshop: "ecommerce",
  hotel: "hospitality",
  travel: "hospitality",
  tourism: "hospitality",
  food: "food-beverage",
  restaurant: "food-beverage",
  beverage: "food-beverage",
  grill: "food-beverage",
  farm: "food-beverage",
  edu: "education",
  academy: "education",
  learning: "education",
  public: "government",
  police: "government",
  civic: "government",
  advertisement: "marketing",
  advertising: "marketing",
  media: "entertainment",
  music: "entertainment",
  creative: "entertainment",
  property: "real-estate",
  beauty: "lifestyle",
  fashion: "lifestyle",
  styling: "lifestyle",
};

/** Default segments when not set in project frontmatter (edit or move to YAML over time). */
export const PROJECT_SEGMENT_ASSIGNMENTS: Partial<Record<string, ProjectSegmentId[]>> = {
  "3d": ["technology", "ecommerce"],
  "akademia-fenix": ["education"],
  alloy: ["technology"],
  "alloy-identity": ["technology"],
  "alloy-mobile": ["technology"],
  aspendos: ["ecommerce", "lifestyle"],
  bbq: ["food-beverage", "ecommerce"],
  "bejo-grill": ["food-beverage"],
  "bio-farma": ["food-beverage", "education"],
  bobostyling: ["lifestyle"],
  "button-systems": ["technology"],
  danyshop: ["ecommerce", "food-beverage"],
  designair: ["automotive"],
  "east-air": ["hospitality"],
  edm: ["entertainment"],
  ejedlo: ["food-beverage"],
  elisabeth: ["hospitality"],
  "elisabeth-mobile": ["hospitality"],
  eoil: ["automotive", "ecommerce"],
  guvernante: ["automotive"],
  hankadanka: ["hospitality"],
  "hubneme-ve-dvou": ["healthcare", "lifestyle"],
  hudbazdola: ["entertainment"],
  industryphoto: ["technology"],
  intobridge: ["technology"],
  kadernictvoparis: ["lifestyle"],
  kaiserhoff: ["hospitality"],
  katastervpapuciach: ["government"],
  lacnaubytovna: ["hospitality"],
  lececil: ["lifestyle", "entertainment"],
  map4nomads: ["hospitality", "technology"],
  mattcoffin: ["finance"],
  maxinn: ["hospitality"],
  mikona: ["ecommerce"],
  micina: ["lifestyle"],
  mojapolicia: ["government"],
  "nasi-poslanci": ["government"],
  niejehriech: ["lifestyle"],
  parkhotel: ["hospitality", "marketing"],
  pinkwhale: ["entertainment"],
  polyphonicabbys: ["entertainment"],
  prestige: ["lifestyle"],
  slovakfinance: ["automotive"],
  slavnosti: ["entertainment", "marketing"],
  subclub: ["entertainment"],
  trila: ["finance", "marketing"],
  superunuseful: ["entertainment", "ecommerce"],
  "superunuseful-app": ["entertainment", "technology"],
  toonect: ["technology"],
  "toonect-identity": ["technology"],
  "toonect-mobile": ["technology"],
  "vesmirny-motion": ["marketing", "technology"],
  vydumamky: ["education"],
  wildpoppy: ["ecommerce"],
  zlobivehovinko: ["education"],
  bdrtg: ["technology", "other"],
};

export function getSegmentLabel(id: string): string {
  return PROJECT_SEGMENTS.find((s) => s.id === id)?.label ?? id;
}

export function normalizeSegment(value: string): ProjectSegmentId | null {
  const key = value.toLowerCase().trim().replace(/\s+/g, "-");
  if (SEGMENT_IDS.has(key)) return key as ProjectSegmentId;
  return SEGMENT_ALIASES[key] ?? null;
}

export function normalizeSegments(values: string[]): ProjectSegmentId[] {
  const out = new Set<ProjectSegmentId>();
  for (const value of values) {
    const segment = normalizeSegment(value);
    if (segment) out.add(segment);
  }
  return [...out];
}

/** Parse legacy `**Industry:**` lines from markdown body. */
export function inferSegmentsFromBody(body: string): ProjectSegmentId[] {
  const match = body.match(/\*\*Industry:\*\*\s*(.+)/i);
  if (!match) return [];

  const parts = match[1].split(/[,;|/]+/).map((p) => p.trim()).filter(Boolean);
  return normalizeSegments(parts);
}

export function resolveProjectSegments(
  data: Record<string, unknown>,
  projectId: string,
  body: string,
): ProjectSegmentId[] {
  const fromYaml = parseStringArray(data.segments ?? data.segment);
  const yamlSegments = normalizeSegments(fromYaml);

  if (yamlSegments.length > 0) return yamlSegments;

  const fromBody = inferSegmentsFromBody(body);
  if (fromBody.length > 0) return fromBody;

  return PROJECT_SEGMENT_ASSIGNMENTS[projectId] ?? [];
}

function parseStringArray(value: unknown): string[] {
  if (typeof value === "string") return [value];
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is string => typeof item === "string");
}

/** Segments that appear on at least one project, for filter chips. */
export function getSegmentsInUse(
  projects: { segments: ProjectSegmentId[] }[],
): (typeof PROJECT_SEGMENTS)[number][] {
  const used = new Set<ProjectSegmentId>();
  for (const project of projects) {
    for (const segment of project.segments) {
      used.add(segment);
    }
  }
  return PROJECT_SEGMENTS.filter((s) => used.has(s.id));
}
